import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface EmailAttachment {
  id: string;
  email_id: string;
  lead_id: string;
  filename: string;
  content_type: string;
  size: number;
  storage_path: string;
  created_at: string;
}

export const useEmailAttachments = (emailId: string) => {
  const queryClient = useQueryClient();

  const { data: attachments, isLoading, error } = useQuery({
    queryKey: ["email-attachments", emailId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("lead_email_attachments")
        .select("*")
        .eq("email_id", emailId)
        .order("created_at", { ascending: true });

      if (error) throw error;
      return data as EmailAttachment[];
    },
    enabled: !!emailId,
  });

  // Generate a signed URL for downloading/viewing an attachment
  const getDownloadUrl = async (storagePath: string): Promise<string | null> => {
    const { data, error } = await supabase.storage
      .from("documents")
      .createSignedUrl(storagePath, 60 * 60); // 1 hour expiry

    if (error) {
      console.error("Error creating signed URL:", error);
      return null;
    }

    return data?.signedUrl || null;
  };

  // Save attachment to the lead's document vault
  const saveToDocumentsMutation = useMutation({
    mutationFn: async (attachment: EmailAttachment) => {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Copy the file from email-attachments to user documents folder
      const sourcePath = attachment.storage_path;
      const destPath = `${user.id}/${attachment.filename}`;

      // Download the file content
      const { data: fileData, error: downloadError } = await supabase.storage
        .from("documents")
        .download(sourcePath);

      if (downloadError) throw downloadError;

      // Upload to new location
      const { error: uploadError } = await supabase.storage
        .from("documents")
        .upload(destPath, fileData, {
          contentType: attachment.content_type,
          upsert: false,
        });

      // If file exists, try with a unique name
      if (uploadError && uploadError.message.includes("already exists")) {
        const timestamp = Date.now();
        const ext = attachment.filename.split('.').pop();
        const baseName = attachment.filename.replace(`.${ext}`, '');
        const uniquePath = `${user.id}/${baseName}-${timestamp}.${ext}`;

        const { error: retryError } = await supabase.storage
          .from("documents")
          .upload(uniquePath, fileData, {
            contentType: attachment.content_type,
          });

        if (retryError) throw retryError;

        // Create document record with unique path
        const { error: docError } = await supabase
          .from("documents")
          .insert({
            lead_id: attachment.lead_id,
            user_id: user.id,
            file_name: `${baseName}-${timestamp}.${ext}`,
            file_path: uniquePath,
            file_type: attachment.content_type,
            file_size: attachment.size,
            notes: "Saved from email attachment",
          });

        if (docError) throw docError;
        return;
      }

      if (uploadError) throw uploadError;

      // Create document record
      const { error: docError } = await supabase
        .from("documents")
        .insert({
          lead_id: attachment.lead_id,
          user_id: user.id,
          file_name: attachment.filename,
          file_path: destPath,
          file_type: attachment.content_type,
          file_size: attachment.size,
          notes: "Saved from email attachment",
        });

      if (docError) throw docError;
    },
    onSuccess: (_, attachment) => {
      toast.success(`"${attachment.filename}" saved to Documents`);
      queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
    onError: (error: Error) => {
      console.error("Error saving to documents:", error);
      toast.error("Failed to save attachment to Documents");
    },
  });

  return {
    attachments: attachments || [],
    isLoading,
    error,
    getDownloadUrl,
    saveToDocuments: saveToDocumentsMutation.mutate,
    isSaving: saveToDocumentsMutation.isPending,
  };
};
